import { type ReactElement, useCallback, useEffect, useState } from 'react';
import {
  COMPENSATION_REASONS,
  CONTACT_REVEAL_PURPOSES,
  TICKET_RESOLUTIONS,
  type SupportParty,
  type SupportTicketDetail,
} from '@vello/api-client';
import { TICKET_REOPEN_REASONS, applyTicketAction, canReopen, canWork, deskPrimaryAction, dueInParts, formatMarketInstant, grantsAllow, marketTimeZone, priorityTone, revealOptions, slaBadge, ticketReference, ticketAge, ticketStatusTone } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, Field, MoneyInput, Notice, PageHeader, Panel, Select, TextArea, TextInput, FormRow, SectionTitle, Cluster, Grid, Stack, Text, Card, BulletList, HairlineRow, PreWrap } from '@vello/web-theme';
import { SubjectCard } from '../components/subject-card';
import { LoadingBlock } from '../components/loading-block';
import { CentralLink } from '../components/central-link';

/**
 * Support › Ticket detail (W5-SUPPORT-DESK, D384, ADR-0054). The atendente handles one ticket end to end:
 *
 * - the SUBJECT CARD is the server's support projection (name, masked phone, status, role; a driver's vehicle; a desk
 *   guest's first name and partner account) — the full phone appears only after a logged reveal, which the server allows
 *   only on an open ticket assigned to the caller, for a purpose picked in the design-system confirm dialog;
 * - INTERNAL NOTES are a timeline only the team reads;
 * - the ONE primary action follows the state (`deskPrimaryAction`): assign to me → resolve (or resume a ticket waiting
 *   for the customer) → close; waiting for the customer, linking a ride and asking Finance for a compensation are
 *   secondary;
 * - money: in a cash-only market (the governed payment policy) the agent moves no money — the panel says so and offers the
 *   compensation request; elsewhere a refund/coupon is recorded as an intent in the MARKET currency (never typed), and the
 *   notice says nothing was paid back yet.
 */
export function TicketDetailScreen(): ReactElement {
  const { runtime, grants, markets } = useAdmin();
  const { params, navigate, goBack } = useRouter();
  const { toast, confirm, setRecordTitle } = useConsole();
  const { t, lang } = useT();
  const { v, fail, reason, hasReason } = useVocab();
  const id = params.id ?? '';
  const [detail, setDetail] = useState<SupportTicketDetail | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [revealed, setRevealed] = useState<Partial<Record<SupportParty, string>>>({});
  const [note, setNote] = useState('');
  const [publicReply, setPublicReply] = useState('');
  const [rideId, setRideId] = useState('');
  const [busy, setBusy] = useState(false);
  const [moneyKind, setMoneyKind] = useState<'REFUND' | 'COUPON'>('REFUND');
  const [moneyAmount, setMoneyAmount] = useState<bigint | undefined>(undefined);
  const [moneyReason, setMoneyReason] = useState('');
  const [moneyMsg, setMoneyMsg] = useState<{ tone: 'success' | 'warning' | 'danger'; text: string } | undefined>(undefined);

  const load = useCallback(async (): Promise<void> => {
    const res = await runtime.supportDesk.ticket(id);
    if (res.ok) {
      setDetail(res.value);
      setError(undefined);
      // D384 review (W5S-08): a revealed phone stays on screen only while the server still allows a reveal (open and mine).
      if (!res.value.contactReveal.allowed) setRevealed({});
      setRecordTitle(t('desk.detail.title', { id: ticketReference(res.value.ticket) }));
    } else {
      setDetail(undefined);
      setError(res.error.code === 'NOT_FOUND' || res.error.code === 'FORBIDDEN' ? t('desk.detail.notFound') : t('desk.detail.error', { code: fail(res.error) }));
    }
  }, [runtime, id, setRecordTitle, t]);

  useEffect(() => {
    if (id !== '') void load();
    return () => setRecordTitle(undefined);
  }, [id, load, setRecordTitle]);

  const done = async (res: { ok: boolean; error?: { code: string; reasonCode?: string } }, okMessage = t('desk.action.done')): Promise<void> => {
    if (res.ok) toast({ message: okMessage, icon: 'check', tone: 'success' });
    else toast({ message: t('desk.action.failed', { code: res.error !== undefined ? fail(res.error) : '' }), icon: 'shieldAlert', tone: 'warning' });
    await load();
  };
  const run = async (fn: () => Promise<{ ok: boolean; error?: { code: string; reasonCode?: string } }>, okMessage?: string): Promise<void> => {
    setBusy(true);
    const res = await fn();
    setBusy(false);
    await done(res, okMessage);
  };

  if (error !== undefined) {
    return (
      <>
        <PageHeader title={t('cases.title')} actions={<Button variant="ghost" icon="arrowLeft" onClick={() => goBack('cases')}>{t('desk.detail.back')}</Button>} />
        <Notice tone="danger">{error}</Notice>
      </>
    );
  }
  if (detail === undefined) return <LoadingBlock />;

  const tk = detail.ticket;
  // D420 (Admin checklist #2): the ticket's own market zone, never a sliced UTC string.
  const ticketZone = marketTimeZone(tk.marketId, markets);
  const primary = deskPrimaryAction(tk);
  const working = canWork(tk);
  const nowIso = new Date().toISOString();
  const age = ticketAge(tk.createdAt, nowIso);
  const reveals = revealOptions(detail);
  // D448 (S-03): the server's SLA clock as one badge.
  const slaTone = slaBadge({ ...(detail.slaState !== undefined ? { slaState: detail.slaState } : {}) }, nowIso);
  const dueParts = dueInParts(slaTone.minutes ?? 0);
  const dueIn = t(`desk.age.${dueParts.unit}`, { n: String(dueParts.value) });

  const assign = (): Promise<void> => run(() => runtime.supportDesk.assignToSelf(tk.ticketId));
  const resolve = async (): Promise<void> => {
    const labels = TICKET_RESOLUTIONS.map((c) => v('support.ticket_resolution', c));
    const r = await confirm({ title: t('desk.resolve.title'), body: t('desk.resolve.body'), confirmLabel: t('desk.resolve.confirm'), cancelLabel: t('ui.cancel'), icon: 'check', reasons: labels, reasonLabel: t('desk.resolve.reasonLabel'), reasonsOnly: true });
    const code = r.confirmed ? TICKET_RESOLUTIONS[labels.indexOf(r.reason ?? '')] : undefined;
    if (code !== undefined) await run(() => runtime.supportDesk.transition(tk.ticketId, { to: 'RESOLVED', resolutionCode: code }));
  };
  const close = async (): Promise<void> => {
    const r = await confirm({ title: t('desk.close.title'), body: t('desk.close.body'), confirmLabel: t('desk.close.confirm'), cancelLabel: t('ui.cancel'), icon: 'lock' });
    if (r.confirmed) await run(() => runtime.supportDesk.transition(tk.ticketId, { to: 'CLOSED' }));
  };
  const wait = (): Promise<void> => run(() => runtime.supportDesk.transition(tk.ticketId, { to: 'PENDING' }));
  // D384 review (W5S-05): a finished ticket is reopened through the existing audited `REOPEN` action, with a reason code.
  const mayReopen = canReopen(tk) && (grants === undefined || grantsAllow(grants, { permission: 'support:respond', target: 'GEO' }));
  const reopen = async (): Promise<void> => {
    const labels = TICKET_REOPEN_REASONS.map((c) => v('support.reopen_reason', c));
    const r = await confirm({ title: t('desk.reopen.title'), body: t('desk.reopen.body'), confirmLabel: t('desk.reopen.confirm'), cancelLabel: t('ui.cancel'), icon: 'rotateCcw', reasons: labels, reasonLabel: t('desk.reopen.reasonLabel'), reasonsOnly: true });
    const code = r.confirmed ? TICKET_REOPEN_REASONS[labels.indexOf(r.reason ?? '')] : undefined;
    if (code === undefined) return;
    setBusy(true);
    const out = await applyTicketAction(runtime, tk.ticketId, { action: 'REOPEN', reasonCode: code });
    setBusy(false);
    if (out.ok) toast({ message: t('cases.reopened'), icon: 'check', tone: 'success' });
    else toast({ message: t('desk.action.failed', { code: out.reasonCode !== undefined ? reason(out.reasonCode) : '' }), icon: 'shieldAlert', tone: 'warning' });
    await load();
  };
  const askCompensation = async (): Promise<void> => {
    const labels = COMPENSATION_REASONS.map((c) => v('support.compensation_reason', c));
    const r = await confirm({ title: t('desk.compensation.title'), body: t('desk.compensation.body'), confirmLabel: t('desk.compensation.confirm'), cancelLabel: t('ui.cancel'), icon: 'badgeCheck', reasons: labels, reasonLabel: t('desk.compensation.reasonLabel'), reasonsOnly: true });
    const code = r.confirmed ? COMPENSATION_REASONS[labels.indexOf(r.reason ?? '')] : undefined;
    if (code !== undefined) await run(() => runtime.supportDesk.requestCompensation(tk.ticketId, code), t('desk.compensation.sent'));
  };
  const reveal = async (party: SupportParty, name: string): Promise<void> => {
    const labels = CONTACT_REVEAL_PURPOSES.map((p) => v('customer_360.purpose', p));
    const r = await confirm({ title: t('desk.reveal.title', { name }), body: t('desk.reveal.body'), confirmLabel: t('desk.reveal.confirm'), cancelLabel: t('ui.cancel'), icon: 'lock', reasons: labels, reasonLabel: t('desk.reveal.purposeLabel'), reasonsOnly: true });
    const purpose = r.confirmed ? CONTACT_REVEAL_PURPOSES[labels.indexOf(r.reason ?? '')] : undefined;
    if (purpose === undefined) return;
    const res = await runtime.supportDesk.revealContact(tk.ticketId, { party, purpose });
    if (res.ok) {
      setRevealed((cur) => ({ ...cur, [party]: res.value.phoneE164 }));
      toast({ message: t('desk.reveal.logged'), icon: 'check', tone: 'neutral' });
    } else {
      const known = res.error.reasonCode !== undefined && hasReason(res.error.reasonCode);
      toast({ message: known ? reason(res.error.reasonCode) : t('desk.reveal.refused.other', { code: fail(res.error) }), icon: 'shieldAlert', tone: 'warning' });
    }
    await load();
  };
  const addNote = async (): Promise<void> => {
    setBusy(true);
    const res = await runtime.supportDesk.addNote(tk.ticketId, note);
    setBusy(false);
    if (res.ok) {
      setNote('');
      toast({ message: t('desk.notes.saved'), icon: 'check', tone: 'success' });
      await load();
    } else toast({ message: t('desk.notes.failed', { code: fail(res.error) }), icon: 'shieldAlert', tone: 'warning' });
  };
  const sendReply = async (): Promise<void> => {
    setBusy(true);
    const res = await runtime.supportDesk.reply(tk.ticketId, publicReply);
    setBusy(false);
    if (res.ok) {
      setPublicReply('');
      toast({ message: t('desk.replies.sent'), icon: 'check', tone: 'success' });
      await load();
    } else toast({ message: t('desk.replies.failed', { code: fail(res.error) }), icon: 'shieldAlert', tone: 'warning' });
  };
  const linkRide = async (): Promise<void> => {
    await run(() => runtime.supportDesk.linkRide(tk.ticketId, rideId.trim()), t('desk.link.done'));
    setRideId('');
  };
  const recordMoney = async (): Promise<void> => {
    // D840: the action is one the server still offers (its rail exists).
    const offered = detail.moneyActions.available ? (detail.moneyActions.actions ?? ['REFUND', 'COUPON']) : [];
    const kind = offered.includes(moneyKind) ? moneyKind : offered[0];
    if (moneyAmount === undefined || kind === undefined) return;
    setBusy(true);
    // The currency is the market's: the server takes it from the governed config and refuses another one.
    const out = await applyTicketAction(runtime, tk.ticketId, { action: kind, reasonCode: moneyReason.trim(), amountMinor: moneyAmount.toString() });
    setBusy(false);
    if (out.ok) setMoneyMsg({ tone: out.recorded === 'INTENT' ? 'warning' : 'success', text: out.recorded === 'INTENT' ? t('desk.money.intent') : t('desk.action.done') });
    else if (out.escalationRequired) setMoneyMsg({ tone: 'warning', text: t('desk.money.escalation') });
    else setMoneyMsg({ tone: 'danger', text: t('desk.action.failed', { code: out.reasonCode !== undefined ? reason(out.reasonCode) : '' }) });
    await load();
  };

  const primaryButton = (() => {
    if (primary === 'assign') return <Button variant="primary" disabled={busy} onClick={() => void assign()}>{t('desk.action.assign')}</Button>;
    if (primary === 'resume') return <Button variant="primary" disabled={busy} onClick={() => void assign()}>{t('desk.action.resume')}</Button>;
    if (primary === 'resolve') return <Button variant="primary" disabled={busy} onClick={() => void resolve()}>{t('desk.action.resolve')}</Button>;
    if (primary === 'close') return <Button variant="primary" disabled={busy} onClick={() => void close()}>{t('desk.action.close')}</Button>;
    return undefined;
  })();

  const row = (k: string, v: ReactElement | string): ReactElement => (
    <HairlineRow key={k}>
      <Text role="caption" tone="secondary">{k}</Text>
      <Text role="caption" className="vello-ab-end">{v}</Text>
    </HairlineRow>
  );
  const section = (title: string, children: ReactElement, hint?: string): ReactElement => (
    <Panel>
      <Stack gap={4}>
        <SectionTitle>{title}</SectionTitle>
        {hint !== undefined && <Text as="p" role="caption" tone="tertiary">{hint}</Text>}
        {children}
      </Stack>
    </Panel>
  );
  const money = detail.moneyActions;

  return (
    <>
      <PageHeader
        title={t('desk.detail.title', { id: ticketReference(tk) })}
        subtitle={`${v('support.support_category', tk.category)} · ${tk.channel !== undefined ? v('support.support_channel', tk.channel) : t('desk.subject.REQUESTER')}`}
        actions={
          <Cluster gap={2} wrap>
            <Button variant="ghost" icon="arrowLeft" onClick={() => goBack('cases')}>{t('desk.detail.back')}</Button>
            {working && tk.status !== 'PENDING' && <Button variant="secondary" disabled={busy} onClick={() => void wait()}>{t('desk.action.wait')}</Button>}
            {mayReopen && <Button variant="secondary" icon="rotateCcw" disabled={busy} onClick={() => void reopen()}>{t('desk.action.reopen')}</Button>}
            {primaryButton}
          </Cluster>
        }
      />
      {/* D637: a ticket raised in an app (or by the system) is in the customer's Atendimento in the Central - the link opens that conversation. */}
      <CentralLink kind="ticket" recordId={tk.ticketId} />
      {tk.assignedAgent !== undefined && !tk.assignedToMe && <Notice tone="neutral">{t('desk.action.otherAgent')}</Notice>}

      <Grid min="md" gap={4}>
        {/* The panels of a column sit in a Stack: a bare <div> parent gives them no gap, so Resumo / Prazos / Sobre quem é touched each other. */}
        <Stack gap={4}>
          {section(t('desk.detail.summary'), (
            <div>
              {row(t('cases.col.status'), <Badge tone={ticketStatusTone(tk.status)}>{v('support.ticket_status', tk.status)}</Badge>)}
              {row(t('desk.detail.priority'), <Badge tone={priorityTone(tk.priority)}>{v('support.ticket_priority', tk.priority)}</Badge>)}
              {row(t('desk.detail.age'), <span>{t(`desk.age.${age.unit}`, { n: String(age.value) })}</span>)}
              {row(t('desk.detail.assignee'), tk.assignedToMe ? t('desk.assignee.me') : tk.assignedAgent !== undefined ? t('desk.assignee.other') : t('desk.assignee.none'))}
              {tk.resolutionCode !== undefined && row(t('desk.detail.resolution'), v('support.ticket_resolution', tk.resolutionCode))}
              {row(t('desk.detail.ride'), tk.rideId !== undefined ? <Button variant="ghost" onClick={() => navigate('ride_detail', { id: tk.rideId ?? '' })}>{t('desk.detail.openRide')}</Button> : t('desk.detail.noRide'))}
              {tk.compensation !== undefined && <Notice tone="warning">{t('desk.compensation.flagged', { reason: v('support.compensation_reason', tk.compensation.reasonCode) })}</Notice>}
            </div>
          ))}
          {/* D448 (S-03): the SLA promised on this ticket, where it stands, and its escalations. */}
          {tk.sla !== undefined && section(t('desk.sla.title'), (
            <div>
              {row(t('desk.sla.state'), <Badge tone={slaTone.tone}>{slaTone.messageId === 'sla.dueIn' ? t('sla.dueIn', { time: dueIn }) : t(slaTone.messageId)}</Badge>)}
              {row(t('desk.sla.firstResponse'), tk.firstRespondedAt !== undefined ? t('desk.sla.answeredAt', { at: formatMarketInstant(tk.firstRespondedAt, ticketZone, lang) }) : <span className="tnum">{formatMarketInstant(tk.sla.firstResponseDue, ticketZone, lang)}</span>)}
              {row(t('desk.sla.resolution'), <span className="tnum">{formatMarketInstant(tk.sla.resolutionDue, ticketZone, lang)}</span>)}
              {(tk.escalations ?? []).map((e) => (
                <div key={`${e.reasonCode}-${e.at}`}>{row(t('sla.escalated'), t('desk.sla.escalation', { at: formatMarketInstant(e.at, ticketZone, lang), from: v('support.ticket_priority', e.fromPriority), to: v('support.ticket_priority', e.toPriority), reason: v('support.sla_escalation_reason', e.reasonCode) }))}</div>
              ))}
              {tk.sla.policyVersion !== undefined && row(t('desk.sla.policy'), <span className="tnum">{tk.sla.policyVersion}</span>)}
            </div>
          ))}
          {section(t('desk.detail.subject'), (
            <Stack gap={3}>
              {!detail.contactReveal.allowed && detail.contactReveal.parties.length > 0 && (
                <Notice tone="neutral">{detail.contactReveal.reasonCode === 'CONTACT_REVEAL_TICKET_NOT_OPEN' ? t('desk.reveal.notOpen') : detail.contactReveal.reasonCode === 'CONTACT_REVEAL_TICKET_WAITING' ? t('desk.reveal.waiting') : t('desk.reveal.needsAssign')}</Notice>
              )}
              <SubjectCard card={detail.subjectCard} revealable={reveals} revealed={revealed} onReveal={(party, name) => void reveal(party, name)} />
            </Stack>
          ))}
          {detail.reveals.length > 0 && section(t('desk.reveals.title'), (
            <BulletList>
              {detail.reveals.map((r) => (
                <li key={r.revealId}>
                  <span className="tnum">{formatMarketInstant(r.createdAt, ticketZone, lang)}</span> — {t('desk.reveals.entry', { who: r.byMe ? t('desk.who.me') : t('desk.who.other'), party: v('audit.contact_party', r.party), purpose: v('customer_360.purpose', r.purpose) })}
                </li>
              ))}
            </BulletList>
          ))}
        </Stack>

        <Stack gap={4}>
          {/* D448 (S-01): the conversation the requester reads in the app — never an internal note. */}
          {tk.selfFiled === true && section(t('desk.replies.title'), (
            <Stack gap={3}>
              {(detail.replies ?? []).length === 0 ? (
                <Text as="p" role="caption" tone="tertiary">{t('desk.replies.empty')}</Text>
              ) : (
                <Stack as="ol" gap={2} aria-label={t('desk.replies.title')}>
                  {(detail.replies ?? []).map((r) => (
                    <Card as="li" padding="sm" key={r.replyId} className={r.author === 'AGENT' ? 'vello-ab-tinted' : undefined}>
<Stack gap={2}>
                      <Text as="div" role="caption" tone="secondary">
                        <span>{r.author === 'REQUESTER' ? t('desk.replies.requester') : r.byMe === true ? t('desk.who.me') : t('desk.replies.team')}</span> · <span className="tnum">{formatMarketInstant(r.createdAt, ticketZone, lang)}</span>
                      </Text>
                      <PreWrap>{r.body}</PreWrap>
                    </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
              {detail.publicReply?.allowed === true ? (
                <>
                  <Field label={t('desk.replies.label')} htmlFor="desk-reply">
                    <TextArea id="desk-reply" value={publicReply} maxLength={2000} onChange={(e) => setPublicReply(e.target.value)} />
                  </Field>
                  <Button variant="secondary" disabled={busy || publicReply.trim() === ''} onClick={() => void sendReply()}>{t('desk.replies.send')}</Button>
                </>
              ) : (
                detail.publicReply?.reasonCode !== undefined && <Notice tone="neutral">{reason(detail.publicReply.reasonCode)}</Notice>
              )}
            </Stack>
          ), t('desk.replies.hint'))}
          {section(t('desk.notes.title'), (
            <Stack gap={3}>
              {detail.notes.length === 0 ? (
                <Text as="p" role="caption" tone="tertiary">{t('desk.notes.empty')}</Text>
              ) : (
                <Stack as="ol" gap={2} aria-label={t('desk.notes.title')}>
                  {detail.notes.map((n) => (
                    <Card as="li" padding="sm" key={n.noteId}>
                      <Stack gap={3}>
                        <Text as="div" role="caption" tone="secondary">
                          <span>{n.byMe ? t('desk.who.me') : t('desk.who.other')}</span> · <span className="tnum">{formatMarketInstant(n.createdAt, ticketZone, lang)}</span>
                        </Text>
                        <PreWrap>{n.body}</PreWrap>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
              {tk.status !== 'CLOSED' && (
                <>
                  <Field label={t('desk.notes.label')} htmlFor="desk-note">
                    <TextArea id="desk-note" value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  <Button variant="secondary" disabled={busy || note.trim() === ''} onClick={() => void addNote()}>{t('desk.notes.add')}</Button>
                </>
              )}
            </Stack>
          ), t('desk.notes.hint'))}

          {working && section(t('desk.link.submit'), (
            <FormRow
              action={
                <Button variant="secondary" disabled={busy || rideId.trim() === ''} onClick={() => void linkRide()}>{t('desk.link.submit')}</Button>
              }
            >
              <Field label={t('desk.link.label')} htmlFor="desk-link-ride">
                <TextInput id="desk-link-ride" value={rideId} onChange={(e) => setRideId(e.target.value)} autoComplete="off" />
              </Field>
            </FormRow>
          ))}

          {working && section(t('desk.money.title'), (
            money.available ? (
              <Stack gap={3}>
                <Text as="p" role="caption" tone="secondary">{t('desk.money.currency', { currency: money.currency })}</Text>
                <Field label={t('desk.money.kind')} htmlFor="desk-money-kind">
                  {/* D840: only the money actions whose rail exists (the server names them). */}
                  <Select id="desk-money-kind" value={moneyKind} onChange={(e) => setMoneyKind(e.target.value === 'COUPON' ? 'COUPON' : 'REFUND')}>
                    {(money.actions ?? ['REFUND', 'COUPON']).map((a) => <option key={a} value={a}>{t(`desk.money.${a}`)}</option>)}
                  </Select>
                </Field>
                <MoneyInput id="desk-money-amount" label={t('desk.money.amount')} currency={money.currency} locale={lang} valueMinor={moneyAmount} onChangeMinor={setMoneyAmount} issueText={(issue) => t(`money.issue.${issue}`)} />
                <Field label={t('desk.money.reason')} htmlFor="desk-money-reason">
                  <TextInput id="desk-money-reason" value={moneyReason} onChange={(e) => setMoneyReason(e.target.value)} />
                </Field>
                {moneyMsg !== undefined && <Notice tone={moneyMsg.tone}>{moneyMsg.text}</Notice>}
                <Button variant="secondary" disabled={busy || moneyAmount === undefined || moneyReason.trim() === ''} onClick={() => void recordMoney()}>{t('desk.money.submit')}</Button>
              </Stack>
            ) : (
              <Stack gap={3}>
                <Notice tone="neutral">{money.reasonCode === 'SUPPORT_MONEY_ACTIONS_CASH_ONLY_MARKET' ? t('desk.money.cashOnly') : money.reasonCode === 'COMPENSATION_RAIL_UNAVAILABLE' ? t('desk.money.noRail') : t('desk.money.unavailable', { code: reason(money.reasonCode) })}</Notice>
                {tk.compensation === undefined && (
                  <Button variant="secondary" disabled={busy} onClick={() => void askCompensation()}>{t('desk.action.compensation')}</Button>
                )}
              </Stack>
            )
          ))}
        </Stack>
      </Grid>
    </>
  );
}
